
import React from 'react';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, ArrowLeft, Check, Play } from "lucide-react";
import TypeAnimation from './TypeAnimation';

export interface StepProps {
  title: string;
  description: string;
  targetElement: string;
  page: 'home' | 'editor' | 'any';
  position: 'top' | 'bottom' | 'left' | 'right';
  offset?: { x: number; y: number };
  width?: number;
  animation?: {
    type: 'type' | 'highlight';
    text?: string;
    color?: string;
    showProcessButton?: boolean;
  };
}

interface OnboardingStepProps {
  step: StepProps;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  onFinish: () => void;
}

const OnboardingStep: React.FC<OnboardingStepProps> = ({
  step,
  currentStep,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
  onFinish,
}) => {
  const [targetRect, setTargetRect] = React.useState<DOMRect | null>(null);
  const [windowHeight, setWindowHeight] = React.useState(window.innerHeight);
  const [windowWidth, setWindowWidth] = React.useState(window.innerWidth);
  
  // Add resize listener
  React.useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
      setWindowWidth(window.innerWidth);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  React.useEffect(() => {
    const targetEl = document.querySelector(step.targetElement);
    if (targetEl) {
      const rect = targetEl.getBoundingClientRect();
      setTargetRect(rect);
      
      // Scroll into view if needed
      if (rect.top < 0 || rect.bottom > windowHeight) {
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [step.targetElement, windowHeight, windowWidth]);

  if (!targetRect) return null;

  const getPopoverStyle = () => {
    const width = step.width || (windowWidth < 768 ? windowWidth * 0.9 : 320);
    let style: React.CSSProperties = {
      position: 'fixed',
      zIndex: 9999,
      width: width,
      maxHeight: '80vh',
      overflowY: 'auto' as const,
    };

    const offset = step.offset || { x: 0, y: 0 };
    const isMobile = windowWidth < 768;

    // For mobile devices, prefer positioning at the bottom or top of the screen
    if (isMobile) {
      // Check if there's more space above or below the target
      const spaceAbove = targetRect.top;
      const spaceBelow = windowHeight - targetRect.bottom;
      
      if (spaceBelow > spaceAbove && spaceBelow > 200) {
        // Position below
        style.left = (windowWidth - width) / 2;
        style.top = targetRect.bottom + 20 + offset.y;
      } else {
        // Position above
        style.left = (windowWidth - width) / 2;
        style.bottom = windowHeight - targetRect.top + 20 - offset.y;
      }
    } else {
      // Desktop positioning
      switch (step.position) {
        case 'top':
          style.left = targetRect.left + (targetRect.width / 2) - (width / 2) + offset.x;
          style.top = targetRect.top - 10 - (style.height as number || 0) + offset.y;
          break;
        case 'bottom':
          style.left = targetRect.left + (targetRect.width / 2) - (width / 2) + offset.x;
          style.top = targetRect.bottom + 10 + offset.y;
          break;
        case 'left':
          style.right = windowWidth - targetRect.left + 10 + offset.x;
          style.top = targetRect.top + (targetRect.height / 2) - ((style.height as number) || 0) / 2 + offset.y;
          break;
        case 'right':
          style.left = targetRect.right + 10 + offset.x;
          style.top = targetRect.top + (targetRect.height / 2) - ((style.height as number) || 0) / 2 + offset.y;
          break;
      }
    }

    // Ensure the popover stays within the viewport
    if (style.left !== undefined) {
      if ((style.left as number) < 10) style.left = 10;
      if ((style.left as number) + width > windowWidth - 10) {
        style.left = windowWidth - width - 10;
      }
    }

    if (style.top !== undefined) {
      if ((style.top as number) < 10) style.top = 10;
      if ((style.top as number) + (style.height as number || 200) > windowHeight - 10) {
        style.top = windowHeight - ((style.height as number) || 200) - 10;
      }
    }

    return style;
  };

  const getHighlightStyle = (): React.CSSProperties => {
    return {
      position: 'fixed',
      top: targetRect.top - 4,
      left: targetRect.left - 4,
      width: targetRect.width + 8,
      height: targetRect.height + 8,
      zIndex: 9998,
      borderRadius: '4px',
      boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
      background: 'transparent',
      pointerEvents: 'none' as const,
    };
  };

  return (
    <>
      <div style={getHighlightStyle()} />
      
      <Card style={getPopoverStyle()} className="p-4 shadow-lg bg-card text-card-foreground">
        <div className="flex justify-between items-center mb-2">
          <h3 className="font-semibold text-foreground">Step {currentStep + 1} of {totalSteps}</h3>
          <Button variant="ghost" size="sm" onClick={onSkip}>
            Skip
          </Button>
        </div>
        
        <h2 className="text-lg font-bold mb-2 text-foreground">{step.title}</h2>
        
        {step.animation?.type === 'type' ? (
          <div className="mb-4">
            <div 
              className="p-2 mb-2 rounded bg-accent/50 text-foreground" 
            >
              <TypeAnimation text={step.animation.text || ''} />
            </div>
            {step.animation.showProcessButton && (
              <div className="flex justify-end mt-2">
                <Button variant="outline" size="sm" disabled>
                  <Play className="h-4 w-4 mr-2" />
                  Process
                </Button>
              </div>
            )}
          </div>
        ) : (
          <p className="mb-4 text-muted-foreground">{step.description}</p>
        )}
        
        <div className="flex justify-between">
          {currentStep > 0 ? (
            <Button variant="outline" onClick={onPrev}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          ) : (
            <div></div>
          )}
          
          {currentStep < totalSteps - 1 ? (
            <Button onClick={onNext}>
              Next
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          ) : (
            <Button onClick={onFinish}>
              Finish
              <Check className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      </Card>
    </>
  );
};

export default OnboardingStep;
